import { Routes, Route, useLocation } from 'react-router-dom'
import { Show, SignInButton, SignUpButton } from '@clerk/react'
import Layout from './components/Layout'
import Studio from './pages/Studio'
import Gallery from './pages/Gallery'
import Datasets from './pages/Datasets'
import Settings from './pages/Settings'
import Broken from './pages/Broken'
import TheUsual from './pages/TheUsual'
import NotFound from './pages/NotFound'
import OutageTakeover from './components/OutageTakeover'
import useOffline from './hooks/useOffline'
import { ChatProvider } from './lib/chatContext'

export default function App() {
  const location = useLocation()
  const { isOnline } = useOffline()

  // Public share route — /broken always renders the outage takeover,
  // regardless of auth state, for shareability (DMs, tweets, etc.).
  if (location.pathname === '/broken') {
    return <Broken />
  }

  // Public share route — /drink renders the art director's signature
  // recipe card, regardless of auth. Same "brand surface" pattern as
  // /broken: a whole page of in-voice flavor meant to be screenshotted.
  if (location.pathname === '/drink') {
    return <TheUsual />
  }

  // Public 404 route — Frank holding broken cables. Reachable directly
  // at /404 for sharing AND used as the signed-in catch-all below.
  if (location.pathname === '/404') {
    return <NotFound />
  }

  // Auto-takeover when the browser reports offline — applies before any
  // Clerk gating so both signed-in and signed-out users get the moment.
  if (!isOnline) {
    return <OutageTakeover forceShow />
  }

  return (
    <>
      <Show when="signed-in">
        <ChatProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<Studio />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/datasets" element={<Datasets />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        </ChatProvider>
      </Show>

      <Show when="signed-out">
        <div className="crt-scanlines flex min-h-screen items-center justify-center bg-bg-primary relative overflow-hidden">
          <div className="absolute inset-0 grid-backdrop pointer-events-none opacity-30" />
          {/* Deep neon orb glow sitting behind Frank for that cathode-ray vibe */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(57,255,20,0.14) 0%, rgba(57,255,20,0.05) 35%, transparent 70%)',
              filter: 'blur(60px)',
              animation: 'pulse-soft 5s ease-in-out infinite',
            }}
          />
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12 px-6 max-w-5xl">
            {/* Frank — the user-avatar, the operator, the lab monster himself */}
            <div className="relative shrink-0">
              <img
                src="/frank.png"
                alt="Frank, the lab's resident operator, at his glowing laptop"
                className="w-56 md:w-72 h-auto rounded-2xl select-none"
                draggable={false}
              />
              <span className="absolute -bottom-2 -right-2 font-mono text-[9px] uppercase tracking-[0.25em] text-neon/70 px-2 py-1 rounded-md bg-bg-primary/80 border border-neon/30">
                ● FRANK · ONLINE
              </span>
            </div>

            {/* Right side — brand + auth */}
            <div className="text-center md:text-left space-y-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon/60">
                / WELCOME TO THE LAB
              </p>
              <h1 className="brand-wordmark text-5xl md:text-6xl text-neon neon-text leading-none">
                FRANKENVIBES <span className="text-text-primary">LAB</span>
              </h1>
              <p className="text-text-secondary text-base md:text-lg italic max-w-md">
                Frank is at his laptop. The art director has poured a second
                rooibos gin. The lab is open.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start pt-1">
                <SignInButton mode="modal">
                  <button className="px-6 py-3 bg-neon text-bg-primary font-semibold rounded-lg hover:bg-neon-dim transition-colors cursor-pointer">
                    Sign In
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button className="px-6 py-3 border border-neon text-neon rounded-lg hover:bg-neon/10 transition-colors cursor-pointer">
                    Sign Up
                  </button>
                </SignUpButton>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted pt-2">
                / project neuro-synthesis · synaptic bridge active
              </p>
            </div>
          </div>
        </div>
      </Show>
    </>
  )
}
